/**
 * Data layer for `saved-views-shareable-urls`. Thin typed fetch wrappers over /api/v1/saved-views-shareable-urls.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SavedViewsShareableUrlsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSavedViewsShareableUrlsResponse {
	count: number;
	items: SavedViewsShareableUrlsRow[];
}

export interface CreateSavedViewsShareableUrlsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/saved-views-shareable-urls";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSavedViewsShareableUrlsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSavedViewsShareableUrlsResponse>(res);
}

export async function create(body: CreateSavedViewsShareableUrlsBody): Promise<SavedViewsShareableUrlsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SavedViewsShareableUrlsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const savedViewsShareableUrlsQueryKeys = {
	all: ["saved-views-shareable-urls"] as const,
	list: () => [...savedViewsShareableUrlsQueryKeys.all] as const,
	detail: (id: string) => [...savedViewsShareableUrlsQueryKeys.all, id] as const,
};
